import React, { useState, useCallback, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRightIcon, ArrowLeftIcon, XIcon, SparklesIcon, MonitorIcon, MailIcon, ArrowUpRightIcon } from "lucide-react";

// --- Types & Data ---
type ProjectType = 'website';
interface TourStep {
  title: string;
  description: string;
  x: number;
  y: number;
}
interface Project {
  id: string;
  name: string;
  type: ProjectType;
  description: string;
  tags: string[];
  screenshot?: string;
  liveUrl?: string;
  accent: string;
  tour?: TourStep[];
}
const projects: Project[] = [{
  id: 'wonders-of-life',
  name: 'Wonders of Life Spa',
  type: 'website',
  description: 'A serene, luxury wellness-spa site designed to feel like the experience itself. Soft lavender palette and atmospheric photography.',
  tags: ['Wellness', 'Luxury', 'Brand Site'],
  screenshot: 'https://cdn.magicpatterns.com/uploads/1xVZ4cruGbPcAxeoaZbD7W/image.png',
  accent: 'from-purple-100 via-white to-lavender-200',
  liveUrl: 'https://project-wondersoflife.magicpatterns.app/'
}, {
  id: 'twm-main',
  name: 'Trippin With Mandy',
  type: 'website',
  description: 'The central hub for all things Mandy. A vibrant, personality-driven portfolio showcasing web design, virtual tours, and creative services.',
  tags: ['Portfolio', 'Personal Brand', 'Creative'],
  screenshot: 'https://cdn.magicpatterns.com/uploads/xhjS3MxsEAmrkrH178kosY/image.png',
  accent: 'from-pink-200 via-rose-100 to-purple-200',
  liveUrl: 'https://trippinwithmandy.com'
}, {
  id: 'twm-agency',
  name: 'TWM Agency',
  type: 'website',
  description: 'A sleek, professional agency front for larger-scale projects. Dark mode aesthetic with neon accents for a modern, high-end feel.',
  tags: ['Agency', 'B2B', 'Dark Mode'],
  screenshot: 'https://cdn.magicpatterns.com/uploads/frLAmDHVoLi1J4ZtDs2fR3/image.png',
  accent: 'from-blue-200 via-indigo-100 to-purple-200',
  liveUrl: 'https://trippinwithmandy.com/agency'
}, {
  id: 'theepinc',
  name: 'The EP Inc',
  type: 'website',
  description: 'A professional, modern web presence built to establish trust and clearly communicate core services.',
  tags: ['Corporate', 'Business', 'Professional'],
  screenshot: 'https://cdn.magicpatterns.com/uploads/ujw8ujVYeEtXbZe7wA3WJy/image.png',
  accent: 'from-blue-100 via-white to-slate-200',
  liveUrl: 'https://theepinc.com'
}];

// --- In-Screen Menu Component ---
function InScreenMenu({
  onSelectTour,
  onSelectLive,
  onClose
}: {
  onSelectTour: (p: Project) => void;
  onSelectLive: (p: Project) => void;
  onClose: () => void;
}) {
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="absolute inset-0 flex flex-col items-center justify-start overflow-y-auto overflow-x-hidden bg-zinc-950 pt-24 sm:p-10 sm:pt-32" data-id="element-0" data-mp="3lkr2vlcb16q7">
      <button onClick={onClose} className="absolute top-16 right-4 sm:top-20 sm:right-6 z-50 rounded-full bg-white/10 p-2 text-white/70 backdrop-blur-md transition-colors hover:bg-white/20 hover:text-white" aria-label="Close menu" data-id="element-1" data-mp="2svjy44m9bkwi">
        <XIcon className="h-5 w-5" data-id="element-2" data-mp="ygmsjissevqo" />
      </button>

      <h2 className="font-[DM_Serif_Display] text-3xl sm:text-5xl text-white mb-2 drop-shadow-md shrink-0 px-4 sm:px-0 text-center" data-id="element-3" data-mp="ydqw5ywl8f4c">Portfolio</h2>
      <p className="text-white/60 text-sm sm:text-base mb-8 shrink-0 px-4 sm:px-0 text-center" data-id="element-4" data-mp="1r5ame2rlqtfu">
        Select a project to explore
      </p>
      
      <div className="grid w-full max-w-4xl shrink-0 gap-6 px-4 pb-12 md:grid-cols-2" data-id="element-5" data-mp="1w0zmc77jacco">
        {projects.map(p => {
        const hasLive = !!p.liveUrl;
        const hasTour = !!p.tour && p.tour.length > 0;
        return <div key={p.id} className="group relative overflow-hidden rounded-xl bg-white/5 border border-white/10 text-left transition duration-300 hover:bg-white/10 hover:border-white/30 hover:shadow-[0_0_30px_rgba(255,255,255,0.1)]" data-id="element-6" data-mp="6ncmyfzirqz0">
              <div className="aspect-[16/10] w-full overflow-hidden" data-id="element-7" data-mp="m5x4ywtp4suo">
                <img src={p.screenshot} alt={p.name} className="h-full w-full object-cover object-top opacity-60 transition duration-700 group-hover:scale-105 group-hover:opacity-100" data-id="element-8" data-mp="m4m2d0bzlr55" />
              </div>
              <div className="absolute inset-0 flex flex-col justify-end bg-gradient-to-t from-black/95 via-black/60 to-transparent p-5 sm:p-6" data-id="element-9" data-mp="19vzv37e1voqn">
                <h3 className="font-[DM_Serif_Display] text-2xl text-white leading-tight" data-id="element-10" data-mp="14xyauj4w1jdl">{p.name}</h3>
                <p className="text-white/70 text-sm mt-2 line-clamp-2" data-id="element-11" data-mp="zwircviczkxb">{p.description}</p>
                <div className="mt-4 flex flex-wrap items-center gap-3" data-id="element-12" data-mp="vv706lju6rn0">
                  {hasTour && <button onClick={() => onSelectTour(p)} className="inline-flex items-center gap-2 rounded-full bg-amber-500/20 border border-amber-500/50 px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-amber-400 transition-colors hover:bg-amber-500 hover:text-white" data-id="element-13" data-mp="3koudd721jk7p">
                      <SparklesIcon className="h-3.5 w-3.5" data-id="element-14" data-mp="2553j9nqfrm2l" /> Take Tour
                    </button>}
                  {hasLive && <button onClick={() => onSelectLive(p)} className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-white transition-colors hover:bg-white hover:text-zinc-900" data-id="element-15" data-mp="29wlfxzlff0hr">
                      <MonitorIcon className="h-3.5 w-3.5" data-id="element-16" data-mp="2ntrvllausj2r" /> Live Site
                    </button>}
                </div>
              </div>
            </div>;
      })}
      </div>
    </motion.div>;
}

// --- In-Screen Live Preview Component ---
function InScreenLivePreview({
  project,
  onClose
}: {
  project: Project;
  onClose: () => void;
}) {
  if (!project.liveUrl) return null;
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="absolute inset-0 flex flex-col bg-zinc-950" data-id="element-17" data-mp="1wsr3w08yalb2">
      {/* Top Bar */}
      <div className="absolute top-0 left-0 right-0 z-30 flex items-center justify-between bg-gradient-to-b from-black/90 to-transparent p-4 pt-16 sm:p-6 sm:pt-20 pointer-events-none" data-id="element-18" data-mp="25tff6n6ym7k9">
         <div className="flex items-center gap-3 pointer-events-auto" data-id="element-19" data-mp="2frd1h559dua8">
           <span className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-white/20 border border-white/50 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white backdrop-blur-md" data-id="element-20" data-mp="2fr3dfn3rxx76">
             <MonitorIcon size={12} data-id="element-21" data-mp="2pj9d7j09piwx" /> Live Site
           </span>
           <h2 className="font-[DM_Serif_Display] text-xl sm:text-2xl text-white drop-shadow-md" data-id="element-22" data-mp="33hpkbb21lbfl">{project.name}</h2>
         </div>
         <button onClick={onClose} className="pointer-events-auto rounded-full bg-black/50 border border-white/20 p-2 text-white hover:bg-white/20 backdrop-blur-md transition-colors" data-id="element-23" data-mp="2yfqfrxukg8h2">
           <XIcon size={18} data-id="element-24" data-mp="3n27p83prwel7" />
         </button>
      </div>

      {/* Iframe Area */}
      <div className="relative h-full w-full bg-white pt-28 sm:pt-32" data-id="element-25" data-mp="384nkmxwsglfk">
        <iframe src={project.liveUrl} title={`${project.name} live preview`} className="h-full w-full border-0" sandbox="allow-scripts allow-same-origin allow-forms allow-popups" data-id="element-26" data-mp="3d40hmbhdj8vv" />
      </div>
    </motion.div>;
}

// --- In-Screen Tour Component ---
function InScreenTour({
  project,
  onClose
}: {
  project: Project;
  onClose: () => void;
}) {
  const [activeStep, setActiveStep] = useState(0);
  const totalSteps = project.tour?.length ?? 0;
  const next = useCallback(() => setActiveStep(s => Math.min(s + 1, totalSteps - 1)), [totalSteps]);
  const prev = useCallback(() => setActiveStep(s => Math.max(s - 1, 0)), []);
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, next, prev]);
  if (!project.tour) return null;
  const step = project.tour[activeStep];
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="absolute inset-0 flex flex-col bg-zinc-950" data-id="element-27" data-mp="2hx1c2r49fval">
      {/* Top Bar */}
      <div className="absolute top-0 left-0 right-0 z-30 flex items-center justify-between bg-gradient-to-b from-black/90 to-transparent p-4 pt-16 sm:p-6 sm:pt-20" data-id="element-28" data-mp="2866ljajze9jc">
         <div className="flex items-center gap-3" data-id="element-29" data-mp="235scbp6n8tjq">
           <span className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-amber-500/20 border border-amber-500/50 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-amber-400 backdrop-blur-md" data-id="element-30" data-mp="1y7h4fjzwst45">
             <MonitorIcon size={12} data-id="element-31" data-mp="9ocv0rdi1c6e" /> Interactive Tour
           </span>
           <h2 className="font-[DM_Serif_Display] text-xl sm:text-2xl text-white drop-shadow-md" data-id="element-32" data-mp="dpq37842drgh">{project.name}</h2>
         </div>
         <button onClick={onClose} className="rounded-full bg-black/50 border border-white/20 p-2 text-white hover:bg-white/20 backdrop-blur-md transition-colors" data-id="element-33" data-mp="iq4486790150">
           <XIcon size={18} data-id="element-34" data-mp="3m72qmmy5w1mn" />
         </button>
      </div>

      {/* Screenshot Area */}
      <div className="relative h-full w-full overflow-hidden" data-id="element-35" data-mp="3r4ur45cvgw2g">
        <img src={project.screenshot} alt={project.name} className="h-full w-full object-cover object-top opacity-80" data-id="element-36" data-mp="3r3b846zu06wz" />
        <div className="absolute inset-0 bg-black/30" data-id="element-37" data-mp="4ptbsyqaiehq" />

        {/* Active Hotspot */}
        <AnimatePresence mode="wait" data-id="element-38" data-mp="4rmek2xs8ekg">
          <motion.div key={activeStep} initial={{
          opacity: 0,
          scale: 0.6
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.6
        }} transition={{
          duration: 0.3
        }} className="absolute z-20" style={{
          left: `${step.x}%`,
          top: `${step.y}%`,
          transform: 'translate(-50%, -50%)'
        }} data-id="element-39" data-mp="33gl8fcaygofj">
            <div className="relative" data-id="element-40" data-mp="2p4rrnvjvgd2c">
              <motion.div animate={{
              scale: [1, 2.5, 1],
              opacity: [0.6, 0, 0.6]
            }} transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeOut'
            }} className="absolute inset-0 h-8 w-8 sm:h-12 sm:w-12 -translate-x-1/2 -translate-y-1/2 rounded-full bg-amber-500" data-id="element-41" data-mp="2p5uense2pvcz" />
              <motion.div animate={{
              scale: [1, 1.8, 1],
              opacity: [0.8, 0, 0.8]
            }} transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeOut',
              delay: 0.4
            }} className="absolute inset-0 h-8 w-8 sm:h-12 sm:w-12 -translate-x-1/2 -translate-y-1/2 rounded-full bg-amber-400" data-id="element-42" data-mp="21ddxdk25u7eq" />
              <div className="relative flex h-8 w-8 sm:h-12 sm:w-12 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-[3px] border-white bg-amber-500 text-xs sm:text-base font-bold text-white shadow-xl" data-id="element-43" data-mp="1iopd90bi4640">
                {activeStep + 1}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Inactive Hotspots */}
        {project.tour.map((s, i) => {
        if (i === activeStep) return null;
        return <button key={i} onClick={() => setActiveStep(i)} className="group absolute z-10" style={{
          left: `${s.x}%`,
          top: `${s.y}%`,
          transform: 'translate(-50%, -50%)'
        }} data-id="element-44" data-mp="3d1682bczyhqp" data-mpi={i}>
              <div className="flex h-6 w-6 sm:h-8 sm:w-8 items-center justify-center rounded-full border-2 border-amber-500 bg-black/60 text-xs sm:text-sm font-bold text-amber-400 shadow-md backdrop-blur-md transition-all group-hover:scale-110 group-hover:bg-amber-500 group-hover:text-white" data-id="element-45" data-mp="348ufaiueui7k">
                {i + 1}
              </div>
            </button>;
      })}
      </div>

      {/* Bottom Control Panel */}
      <div className="absolute bottom-0 left-0 right-0 z-30 bg-black/80 backdrop-blur-xl border-t border-white/10 p-4 sm:p-6 sm:px-8" data-id="element-46" data-mp="2fpsqrwfk6fcr">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-47" data-mp="26nji7c3uow20">
          <div className="flex-1" data-id="element-48" data-mp="2antm8j831ce8">
            <div className="flex items-center gap-2 mb-2" data-id="element-49" data-mp="2aovbpspui1fh">
              <span className="text-[10px] sm:text-xs font-bold uppercase tracking-widest text-amber-500" data-id="element-50" data-mp="2ydflt7sd9tmr">Step {activeStep + 1} of {totalSteps}</span>
              <div className="flex items-center gap-1 ml-2" data-id="element-51" data-mp="33c8zp1bkfmng">
                {project.tour.map((_, i) => <div key={i} className={`h-1.5 rounded-full transition-all ${i === activeStep ? 'w-6 bg-amber-500' : 'w-1.5 bg-white/20'}`} data-id="element-52" data-mp="2pjjev07rwnrx" data-mpi={i} />)}
              </div>
            </div>
            <AnimatePresence mode="wait" data-id="element-53" data-mp="3hap58oxoz740">
              <motion.div key={activeStep} initial={{
              opacity: 0,
              x: 10
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -10
            }} transition={{
              duration: 0.2
            }} data-id="element-54" data-mp="3m41w75pufpzx">
                <h3 className="font-[DM_Serif_Display] text-xl sm:text-2xl text-white mb-1" data-id="element-55" data-mp="15nlzgdqbw6tm">{step.title}</h3>
                <p className="text-xs sm:text-sm text-white/70 line-clamp-2 sm:line-clamp-none" data-id="element-56" data-mp="1ff9lfz448vnp">{step.description}</p>
              </motion.div>
            </AnimatePresence>
          </div>
          
          <div className="flex items-center gap-3 shrink-0 self-end sm:self-auto" data-id="element-57" data-mp="mqlupo0lle2r">
            <button onClick={prev} disabled={activeStep === 0} className="flex items-center justify-center h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-white/10 text-white hover:bg-white/20 disabled:opacity-30 transition-colors" data-id="element-58" data-mp="msowt6f0yxnu">
              <ArrowLeftIcon size={20} data-id="element-59" data-mp="2d16yhgzq88s9" />
            </button>
            <button onClick={next} disabled={activeStep === totalSteps - 1} className="flex items-center justify-center h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-30 transition-colors shadow-[0_0_15px_rgba(245,158,11,0.4)]" data-id="element-60" data-mp="284q80g521ic0">
              <ArrowRightIcon size={20} data-id="element-61" data-mp="qm6qkd1my4zj" />
            </button>
          </div>
        </div>
      </div>
    </motion.div>;
}

// --- Privacy Policy Dialog ---
function PrivacyDialog({
  onClose
}: {
  onClose: () => void;
}) {
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2
  }} className="fixed inset-0 z-[400] flex items-center justify-center bg-black/80 p-4 sm:p-6 backdrop-blur-md" role="dialog" aria-modal="true" aria-labelledby="privacy-title" data-id="element-62" data-mp="15ksaw0mkz8t8">
      <motion.div initial={{
      opacity: 0,
      y: 20,
      scale: 0.95
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} exit={{
      opacity: 0,
      y: 20,
      scale: 0.95
    }} transition={{
      duration: 0.3,
      ease: [0.22, 1, 0.36, 1]
    }} className="relative flex h-full max-h-[85vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-white/10 bg-zinc-900 shadow-2xl" data-id="element-63" data-mp="1jdqega96g77v">
        <div className="flex items-center justify-between border-b border-white/10 bg-zinc-900/80 px-6 py-4 backdrop-blur-sm" data-id="element-64" data-mp="1ohj1qdis4zfc">
          <h2 id="privacy-title" className="font-[DM_Serif_Display] text-2xl text-white" data-id="element-65" data-mp="1khs71yq4tz5j">Privacy Policy</h2>
          <button onClick={onClose} className="inline-flex size-8 items-center justify-center rounded-full bg-white/10 text-white/70 transition-colors hover:bg-white/20 hover:text-white" aria-label="Close privacy dialog" data-id="element-66" data-mp="2c6li7i1ogm89">
            <XIcon size={18} data-id="element-67" data-mp="23e1j6ghh4fmw" />
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto p-6 text-sm leading-relaxed text-white/80 sm:p-8 sm:text-base" data-id="element-68" data-mp="2zwzejkrap78n">
          <div className="mx-auto max-w-3xl space-y-6" data-id="element-69" data-mp="2v3wjf86vdpqc">
            <p className="font-bold text-white" data-id="element-70" data-mp="t7dvh8qynfc6">Websites by Mandy — Privacy Policy</p>
            <p className="text-xs text-white/50" data-id="element-71" data-mp="y70gw5gy01w2">Last Updated: August 15, 2026</p>
            <p data-id="element-72" data-mp="y8uv70xpkvr6">Websites by Mandy respects your privacy and is committed to protecting the personal information you provide when visiting our website, contacting us, or purchasing our services.</p>
            <p data-id="element-73" data-mp="of4bsagfvbic">This Privacy Policy explains what information we may collect, how we use it, when it may be shared, and the choices you have regarding your information.</p>

            <h3 className="pt-4 font-[DM_Serif_Display] text-xl text-white" data-id="element-74" data-mp="fi3q4bkr7ril">1. Information We May Collect</h3>
            <p data-id="element-75" data-mp="l2w21f3bkgy">When you visit Websites by Mandy or contact us about our services, we may collect information that you voluntarily provide, including:</p>
            <ul className="list-disc space-y-2 pl-5" data-id="element-76" data-mp="1uwrguk806h99">
              <li data-id="element-77" data-mp="1uv6iskmhcg6n">Your name</li>
              <li data-id="element-78" data-mp="1zw3z0rnxj05i">Email address</li>
              <li data-id="element-79" data-mp="1zv2p4qingjpd">Phone number</li>
              <li data-id="element-80" data-mp="1zu9ma0mtaeci">Business or company name</li>
              <li data-id="element-81" data-mp="3kdn0mdcnwszx">Information about your business or website project</li>
              <li data-id="element-82" data-mp="3fflf7lrrkhla">Messages or information submitted through contact or inquiry forms</li>
              <li data-id="element-83" data-mp="3u47szj1qt1ae">Files, photographs, logos, written content, or other materials you provide for your website project</li>
              <li data-id="element-84" data-mp="3pczi0ikrl7v4">Billing or transaction information related to services you purchase</li>
              <li data-id="element-85" data-mp="6z8hjsflwlr9">Any other information you voluntarily provide to us</li>
            </ul>
            <p data-id="element-86" data-mp="2xwy64nbswri">We only ask for information that is reasonably necessary to communicate with you or provide the services you request.</p>

            <h3 className="pt-4 font-[DM_Serif_Display] text-xl text-white" data-id="element-87" data-mp="gwmxlqxmb8j1">2. Information Collected Automatically</h3>
            <p data-id="element-88" data-mp="bz4itjwx5z34">When you visit our website, certain technical information may be collected automatically by our website, hosting provider, analytics services, or other technology providers.</p>
            <p data-id="element-89" data-mp="2dr9hrfcu5wnc">This information may include:</p>
            <ul className="list-disc space-y-2 pl-5" data-id="element-90" data-mp="1rwsgc3btz0eu">
              <li data-id="element-91" data-mp="1rw99pirmryg4">IP address</li>
              <li data-id="element-92" data-mp="1ruy4rfmg8knf">Browser type</li>
              <li data-id="element-93" data-mp="1wv3rqihrxiz6">Device type</li>
              <li data-id="element-94" data-mp="1wuat64syxzs4">Operating system</li>
              <li data-id="element-95" data-mp="21mlqczwbr7gz">Pages visited</li>
              <li data-id="element-96" data-mp="21nn5pgkdig4i">Approximate geographic location</li>
              <li data-id="element-97" data-mp="21oya9iln7rwo">Referring website</li>
              <li data-id="element-98" data-mp="26knalo5hlcu1">Date and time of visits</li>
              <li data-id="element-99" data-mp="26nrjuzxt6yrk">General website usage and interaction information</li>
            </ul>
            <p data-id="element-100" data-mp="18zr9ecypuq89">This information may be used to understand how visitors use our website, improve website performance, troubleshoot problems, maintain security, and improve our services.</p>

            <h3 className="pt-4 font-[DM_Serif_Display] text-xl text-white" data-id="element-101" data-mp="1iuj9pojonr6q">3. How We Use Your Information</h3>
            <p data-id="element-102" data-mp="1nwi96ncay3ye">Websites by Mandy may use information we collect to:</p>
            <ul className="list-disc space-y-2 pl-5" data-id="element-103" data-mp="2uahma2e2kvcp">
              <li data-id="element-104" data-mp="2zacce3q0tgwp">Respond to questions and website inquiries</li>
              <li data-id="element-105" data-mp="2z8j5cnbhwuo6">Communicate with potential and existing clients</li>
              <li data-id="element-106" data-mp="nlz3uq0v0k9c">Provide quotes or information about our services</li>
              <li data-id="element-107" data-mp="io61k82ogaec">Design, develop, and complete website projects</li>
              <li data-id="element-108" data-mp="iq0lpk64jyhd">Process and maintain records of transactions</li>
              <li data-id="element-109" data-mp="dvweczsy5ofr">Provide customer support</li>
              <li data-id="element-110" data-mp="8y4bxbuoejxb">Deliver completed projects</li>
              <li data-id="element-111" data-mp="8xbcyx1ykypq">Improve our website, services, and customer experience</li>
              <li data-id="element-112" data-mp="3zjcvfr5u6ag">Prevent fraud, abuse, or security issues</li>
              <li data-id="element-113" data-mp="3woqvjsi0dex">Maintain necessary business and financial records</li>
              <li data-id="element-114" data-mp="3w1955vwxdlg2">Comply with applicable legal requirements</li>
            </ul>
            <p data-id="element-115" data-mp="3r4seatyccsza">Websites by Mandy does not sell your personal information.</p>

            <h3 className="pt-4 font-[DM_Serif_Display] text-xl text-white" data-id="element-116" data-mp="3m55zwh6s16ow">4. Payments</h3>
            <p data-id="element-117" data-mp="1pc4c8nxfobl3">If you purchase services from Websites by Mandy, payments may be processed through a third-party payment provider.</p>
            <p data-id="element-118" data-mp="1kekw1ih6doqi">Websites by Mandy may receive information confirming your transaction, such as your name, payment amount, transaction status, and other information necessary for our business records.</p>
            <p data-id="element-119" data-mp="279kg2suu79tl">Payment processors may collect and process financial information according to their own privacy policies and security practices.</p>
            <p data-id="element-120" data-mp="1xig0ps191my8">Websites by Mandy does not intentionally store complete credit or debit card numbers when payment information is processed directly through a third-party payment processor.</p>

            <h3 className="pt-4 font-[DM_Serif_Display] text-xl text-white" data-id="element-121" data-mp="1tei53rb4uu9x">5. Cookies & Similar Technologies</h3>
            <p data-id="element-122" data-mp="1jif10fwq0lkc">Our website may use cookies or similar technologies to help the website function properly, remember preferences, understand website traffic, improve performance, or provide analytics.</p>
            <p data-id="element-123" data-mp="3dwfyawcmh0ib">Some cookies may be placed by third-party services used on our website.</p>
            <p data-id="element-124" data-mp="38xlgnxqe5hzs">You can usually control or disable cookies through your browser settings. Disabling certain cookies may affect how some portions of the website function.</p>
            <p data-id="element-125" data-mp="3hediuma2wi08">Where required by applicable law, visitors will be provided with appropriate choices regarding non-essential cookies.</p>

            <h3 className="pt-4 font-[DM_Serif_Display] text-xl text-white" data-id="element-126" data-mp="3q77cjoq9vjxm">6. Analytics</h3>
            <p data-id="element-127" data-mp="3v5qyhp4v8vpz">Websites by Mandy may use website analytics services to better understand how visitors find and use our website.</p>
            <p data-id="element-128" data-mp="2ygt3q6x48xst">Analytics providers may collect information such as pages visited, general location, device information, browser information, and interactions with the website.</p>
            <p data-id="element-129" data-mp="37fwnld43xh8d">We use this information to evaluate website performance and improve our services.</p>

            <h3 className="pt-4 font-[DM_Serif_Display] text-xl text-white" data-id="element-130" data-mp="3cg27g744xzfw">7. Third-Party Services</h3>
            <p data-id="element-131" data-mp="mr06xcoarcm7">Websites by Mandy may use third-party companies and technology providers to operate our business and website.</p>
            <p data-id="element-132" data-mp="ro0gvyo9ozil">These may include services for:</p>
            <ul className="list-disc space-y-2 pl-5" data-id="element-133" data-mp="20w9oxvrfriu">
              <li data-id="element-134" data-mp="1zl731e1wptb">Website hosting</li>
              <li data-id="element-135" data-mp="3t7h6yzcervob">Domain and DNS services</li>
              <li data-id="element-136" data-mp="3t6nwpoe8qo1f">Payment processing</li>
              <li data-id="element-137" data-mp="3t9tlt3mvdpx5">Email and communication</li>
              <li data-id="element-138" data-mp="byapqwdrueam">Contact forms</li>
              <li data-id="element-139" data-mp="bykekgn2yfeq">Website analytics</li>
              <li data-id="element-140" data-mp="70ipnf2z3lvc">Cloud storage</li>
              <li data-id="element-141" data-mp="71kagb9sjmq9">Security</li>
              <li data-id="element-142" data-mp="6y6atasihrmi">Website development</li>
              <li data-id="element-143" data-mp="3ff9ehjs5qqvf">Artificial intelligence and creative tools</li>
              <li data-id="element-144" data-mp="3fdpv3kb7fzpf">Other services necessary to operate our business</li>
            </ul>
            <p data-id="element-145" data-mp="3aev620mn9ewv">These providers may process information when necessary to perform services on our behalf.</p>
            <p data-id="element-146" data-mp="3p7oxmkf6xlhw">Third-party companies maintain their own privacy policies and practices, and Websites by Mandy is not responsible for the privacy practices of independent third-party websites or services.</p>

            <h3 className="pt-4 font-[DM_Serif_Display] text-xl text-white" data-id="element-147" data-mp="2rqhtbcczj8s5">8. AI & Technology Tools</h3>
            <p data-id="element-148" data-mp="178fkp5054byh">Websites by Mandy may use artificial intelligence and other technology tools to assist with website design, development, written content, graphics, imagery, video, animation, coding, and other aspects of our services.</p>
            <p data-id="element-149" data-mp="xbaxr2yh0cgm">When reasonably necessary to complete a client's project, information or materials supplied by the client may be processed using third-party technology or AI-assisted services.</p>
            <p data-id="element-150" data-mp="1pzyohe1znu1k">Clients should avoid providing passwords, financial account information, Social Security numbers, medical records, or other highly sensitive personal information unless specifically requested and necessary for the service being provided.</p>
            <p data-id="element-151" data-mp="1h0lgkptijdiy">We make reasonable efforts to limit information shared with third-party tools to information appropriate for completing the requested service.</p>

            <h3 className="pt-4 font-[DM_Serif_Display] text-xl text-white" data-id="element-152" data-mp="5hinqg8dzn5y">9. Client Website Information</h3>
            <p data-id="element-153" data-mp="9m05k9v6rqe3">During a website project, clients may provide Websites by Mandy with business information, photographs, logos, written materials, product information, account information, or other content.</p>
            <p data-id="element-154" data-mp="kz0f8q95kom8">We use these materials for the purpose of completing the client's project and providing requested services.</p>
            <p data-id="element-155" data-mp="b2xcpjae2nyr">Clients are responsible for ensuring they have permission to provide and use any personal information, photographs, copyrighted material, trademarks, or other content they submit to Websites by Mandy.</p>

            <h3 className="pt-4 font-[DM_Serif_Display] text-xl text-white" data-id="element-156" data-mp="13rbema4lm4fl">10. How We Share Information</h3>
            <p data-id="element-157" data-mp="yrgnq6ktpeuj">Websites by Mandy does not sell personal information.</p>
            <p data-id="element-158" data-mp="ur6hqtx16ofo">We may share limited information with service providers when reasonably necessary to:</p>
            <ul className="list-disc space-y-2 pl-5" data-id="element-159" data-mp="3jih1o6411k80">
              <li data-id="element-160" data-mp="3jk21aa577tcq">Operate our website</li>
              <li data-id="element-161" data-mp="3fhdo5q9gc09y">Process payments</li>
              <li data-id="element-162" data-mp="3fgcffsfwrpve">Communicate with clients</li>
              <li data-id="element-163" data-mp="1uz38w8r2uudj">Provide website design and development services</li>
              <li data-id="element-164" data-mp="1v0xsnjqm4gg1">Store project materials</li>
              <li data-id="element-165" data-mp="1z06l8l9x28a0">Maintain security</li>
              <li data-id="element-166" data-mp="1z17woquugws9">Provide other services necessary to operate our business</li>
            </ul>
            <p data-id="element-167" data-mp="23y84p7b989m0">We may also disclose information if required by law, court order, legal process, or when reasonably necessary to protect our rights, customers, business, or others.</p>

            <h3 className="pt-4 font-[DM_Serif_Display] text-xl text-white" data-id="element-168" data-mp="2dojakzei0svj">11. Data Security</h3>
            <p data-id="element-169" data-mp="2inmgsn67t553">Websites by Mandy takes reasonable administrative and technical measures to protect information under our control.</p>
            <p data-id="element-170" data-mp="2mr2d1iedh3h9">However, no website, internet transmission, electronic storage system, or online service can guarantee absolute security.</p>
            <p data-id="element-171" data-mp="schvvfp9fq2m">Clients and visitors should use caution when transmitting sensitive information online and should not send passwords, Social Security numbers, banking credentials, or similarly sensitive information through ordinary contact forms or email unless specifically necessary.</p>

            <h3 className="pt-4 font-[DM_Serif_Display] text-xl text-white" data-id="element-172" data-mp="1vn5hjsdxxmt7">12. Data Retention</h3>
            <p data-id="element-173" data-mp="29mwvxu8dd57q">We may retain personal and business information for as long as reasonably necessary to:</p>
            <ul className="list-disc space-y-2 pl-5" data-id="element-174" data-mp="24o3jm54tjk95">
              <li data-id="element-175" data-mp="24miooeeldzar">Provide requested services</li>
              <li data-id="element-176" data-mp="2jkku6u56ouun">Complete website projects</li>
              <li data-id="element-177" data-mp="2jj9ryyrdzv5n">Maintain project and transaction records</li>
              <li data-id="element-178" data-mp="2emt2a04jh8qg">Respond to future questions regarding completed projects</li>
              <li data-id="element-179" data-mp="2sd6am3lcepgb">Meet accounting, tax, or legal obligations</li>
              <li data-id="element-180" data-mp="2sfiqmb7jipqq">Resolve disputes</li>
              <li data-id="element-181" data-mp="2obazy8hiuzpv">Protect our legitimate business interests</li>
            </ul>
            <p data-id="element-182" data-mp="32blsdfyhcmd5">Information that is no longer reasonably necessary may be deleted or securely disposed of when appropriate.</p>

            <h3 className="pt-4 font-[DM_Serif_Display] text-xl text-white" data-id="element-183" data-mp="2xdahr30d27tv">13. Your Privacy Choices</h3>
            <p data-id="element-184" data-mp="3gy1aujpo02pz">Depending on where you live and applicable privacy laws, you may have certain rights regarding your personal information.</p>
            <p data-id="element-185" data-mp="3lwm9iep9qecb">You may contact Websites by Mandy to request that we:</p>
            <ul className="list-disc space-y-2 pl-5" data-id="element-186" data-mp="36zbq8j6dw461">
              <li data-id="element-187" data-mp="3bz7u8if2zvll">Provide information about personal information we maintain about you</li>
              <li data-id="element-188" data-mp="3c110hwls8dt2">Correct inaccurate information</li>
              <li data-id="element-189" data-mp="2y71fn0rktwo9">Delete certain personal information when legally permitted</li>
              <li data-id="element-190" data-mp="32yrsbddzkwiu">Address a privacy-related question or concern</li>
            </ul>
            <p data-id="element-191" data-mp="2o9vl1ijr7nja">Privacy requests may be submitted to: websitesbymandy@gmail.com</p>
            <p data-id="element-192" data-mp="2t8q5sq1a7m61">Some information may need to be retained for legal, tax, accounting, fraud-prevention, contractual, or legitimate business purposes.</p>

            <h3 className="pt-4 font-[DM_Serif_Display] text-xl text-white" data-id="element-193" data-mp="2fa8jmwr0yhq8">14. Children's Privacy</h3>
            <p data-id="element-194" data-mp="2j994ekt2bufv">Websites by Mandy and its services are intended for adults and businesses seeking website design or related services.</p>
            <p data-id="element-195" data-mp="2znp8hjv3kbj2">We do not knowingly collect personal information directly from children under the age of 13.</p>
            <p data-id="element-196" data-mp="2prvqvm2itruf">If we become aware that personal information from a child under 13 has been collected without appropriate authorization, we will take reasonable steps to delete the information.</p>

            <h3 className="pt-4 font-[DM_Serif_Display] text-xl text-white" data-id="element-197" data-mp="2gr79qihtzbdb">15. Links to Other Websites</h3>
            <p data-id="element-198" data-mp="2c0qtyrqjfdns">Our website may contain links to third-party websites or services.</p>
            <p data-id="element-199" data-mp="271dxdf9v6s80">Once you leave Websites by Mandy, the privacy practices of the third-party website or service apply.</p>
            <p data-id="element-200" data-mp="222t98e7vzyzx">Websites by Mandy is not responsible for the content, security, privacy policies, or privacy practices of websites or services operated by other companies.</p>

            <h3 className="pt-4 font-[DM_Serif_Display] text-xl text-white" data-id="element-201" data-mp="1x5158ltywmfd">16. Changes to This Privacy Policy</h3>
            <p data-id="element-202" data-mp="8juahmhco0j8">Websites by Mandy may update this Privacy Policy as our business, website, technology, services, or legal requirements change.</p>
            <p data-id="element-203" data-mp="cjusfh3nqtv1">When changes are made, the updated version will be posted on this page with a revised “Last Updated” date.</p>
            <p data-id="element-204" data-mp="hi5xlej0cg6x">We encourage visitors to review this Privacy Policy periodically.</p>

            <h3 className="pt-4 font-[DM_Serif_Display] text-xl text-white" data-id="element-205" data-mp="mik956jti89p">17. Contact Us</h3>
            <p data-id="element-206" data-mp="3l2k5bfu6ulyf">If you have questions about this Privacy Policy, have concerns about how your information is handled, or would like to make a privacy-related request, please contact:</p>
            
            <div className="pt-4 pb-4" data-id="element-207" data-mp="3uxkhcfzjkm6i">
              <p className="font-[DM_Serif_Display] text-xl text-white" data-id="element-208" data-mp="3khgmhs29t75">Websites by Mandy</p>
              <p className="mt-2 text-sm text-white/60" data-id="element-209" data-mp="32ahjzr6lqhhu">Email: websitesbymandy@gmail.com</p>
              <p className="text-sm text-white/60" data-id="element-210" data-mp="328n7905hhvou">Website: websitesbymandy.com</p>
              <p className="mt-4 text-xs text-white/40" data-id="element-211" data-mp="377q0lnc3qdgz">Last Updated: August 15, 2026</p>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>;
}

// --- Pricing Dialog ---
function PricingDialog({
  onClose
}: {
  onClose: () => void;
}) {
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.5
  }} className="fixed inset-0 z-[400] flex flex-col items-center justify-center p-4 sm:p-6" role="dialog" aria-modal="true" aria-labelledby="pricing-title" data-id="element-212" data-mp="27y77kevovk5r">
      {/* Close Button */}
      <button onClick={onClose} className="absolute top-6 right-6 z-50 inline-flex size-10 items-center justify-center rounded-full border border-white/20 bg-black/40 text-white/90 shadow-lg backdrop-blur-md transition-colors hover:bg-black/60 hover:text-white" aria-label="Close pricing dialog" data-id="element-213" data-mp="1ob9jk5ri6nxw">
        <XIcon size={20} data-id="element-214" data-mp="1k833k6el9ie8" />
      </button>

      {/* Transparent Content Container */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      y: 20
    }} transition={{
      duration: 0.5,
      delay: 0.2
    }} className="relative z-10 flex h-full max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden" data-id="element-215" data-mp="10mt7cf4an3o5">
        <div className="flex-1 overflow-y-auto p-6 text-sm leading-relaxed text-white drop-shadow-md sm:p-10 sm:text-base [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] [text-shadow:0_2px_5px_rgba(0,0,0,1),0_0_15px_rgba(0,0,0,0.8)]" data-id="element-216" data-mp="15mfr77emni5x">
          <div className="mx-auto max-w-2xl space-y-12 pb-20 font-medium tracking-wide text-center" data-id="element-217" data-mp="1tca3nnv1b4pn">
            
            <section data-id="element-218" data-mp="1ocnh2ntbgc46">
              <h3 id="pricing-title" className="mb-6 font-[DM_Serif_Display] text-5xl text-amber-400 drop-shadow-lg sm:text-6xl" data-id="element-219" data-mp="1oe8c0ejjlx2k">Pricing</h3>
              <p className="mb-8 text-2xl font-bold text-white drop-shadow-lg sm:text-3xl" data-id="element-220" data-mp="22avhj5pk9vk3">
                All websites start at <span className="text-amber-300" data-id="element-221" data-mp="v0fp8lv1s2qs">$300</span>
              </p>
              
              <div className="space-y-6 text-lg sm:text-xl sm:leading-relaxed" data-id="element-222" data-mp="uymfgxp512ek">
                <p data-id="element-223" data-mp="zzscv8tqtduf">
                  Every business is unique, and so is every website. While our base package gets you a beautiful, professional foundation online for $300, most detailed and interactive websites sit between <strong className="text-white" data-id="element-224" data-mp="18tyhlq5mhv5i">$400 and $600</strong>.
                </p>
                <p data-id="element-225" data-mp="1dt1fmqn6r13z">
                  The final price of your custom project is contingent on a few key factors:
                </p>
                
                <ul className="mx-auto max-w-lg space-y-4 text-left list-disc pl-6" data-id="element-226" data-mp="1nouzyw75bywb">
                  <li data-id="element-227" data-mp="1rqgcggfrqb5z"><strong className="text-amber-300" data-id="element-228" data-mp="1rr863lpt1ytm">Detail & Design:</strong> Highly customized graphics, complex layouts, and bespoke branding elements.</li>
                  <li data-id="element-229" data-mp="3pikkhrreg529"><strong className="text-amber-300" data-id="element-230" data-mp="3pikm1w71sd4d">Number of Tabs/Pages:</strong> The overall size of the site and how much content needs to be structured and built.</li>
                  <li data-id="element-231" data-mp="3m04xgngotd3z"><strong className="text-amber-300" data-id="element-232" data-mp="3m04vwj11h51v">Interactivity:</strong> Advanced features like animations, custom forms, booking systems, or interactive tours.</li>
                  <li data-id="element-233" data-mp="3c7z8lmlqfbt0"><strong className="text-amber-300" data-id="element-234" data-mp="3c7z3x9asenmo">Time to Create:</strong> The estimated hours required to design, build, and refine your specific vision.</li>
                </ul>

                <p className="pt-6" data-id="element-235" data-mp="8sk0ws0tteb1">
                  We'll discuss exactly what you need during our consultation and provide a clear, custom quote before any work begins.
                </p>
              </div>
            </section>

            <div className="my-12 h-px w-full bg-white/10" data-id="element-236" data-mp="3vvexx6rftcfn" />

            <section data-id="element-237" data-mp="3qy6fr66ah8ee">
              <h3 className="mb-8 font-[DM_Serif_Display] text-4xl text-amber-400 drop-shadow-lg sm:text-5xl" data-id="element-238" data-mp="3qwlmdjvpnvi4">Monthly Maintenance Plans</h3>
              <p className="mb-10 text-lg sm:text-xl sm:leading-relaxed" data-id="element-239" data-mp="2kfi00t1mfi6a">
                After your website is completed, keep it secure, updated, and running smoothly with an optional maintenance plan.
              </p>

              <div className="grid grid-cols-1 gap-6 text-left md:grid-cols-3" data-id="element-240" data-mp="c0ibh9re8dda">
                {/* Monitoring Plan */}
                <div className="flex flex-col rounded-2xl border border-white/10 bg-black/60 p-6 shadow-lg backdrop-blur-md" data-id="element-241" data-mp="3ufao67pazs0g">
                  <h4 className="font-[DM_Serif_Display] text-2xl text-white" data-id="element-242" data-mp="2bi5h6eh4hpl">Monitoring Plan</h4>
                  <div className="my-4 text-3xl font-bold text-amber-300" data-id="element-243" data-mp="q1kufdn3d7jz">$25<span className="text-lg text-white/50" data-id="element-244" data-mp="uz4a8hzftse1">/mo</span></div>
                  <ul className="mb-6 flex-1 space-y-3 text-sm text-white/80" data-id="element-245" data-mp="ux09ua0qzbii">
                    <li className="flex items-start gap-2" data-id="element-246" data-mp="gzbo6zyuw0ce"><span className="text-amber-500" data-id="element-247" data-mp="lyfv1jyb5lzk">•</span> Website checked twice per month</li>
                    <li className="flex items-start gap-2" data-id="element-248" data-mp="31q3qtc1l58gs"><span className="text-amber-500" data-id="element-249" data-mp="31p11h8jwve2z">•</span> Contact forms tested twice per month</li>
                    <li className="flex items-start gap-2" data-id="element-250" data-mp="36poozk8xlcsg"><span className="text-amber-500" data-id="element-251" data-mp="36mkfq8glzqux">•</span> Basic uptime monitoring</li>
                    <li className="flex items-start gap-2" data-id="element-252" data-mp="2y9zhleposzd7"><span className="text-amber-500" data-id="element-253" data-mp="2u6uh1hp637r7">•</span> Broken links checks</li>
                    <li className="flex items-start gap-2" data-id="element-254" data-mp="385u62qgldonw"><span className="text-amber-500" data-id="element-255" data-mp="38514e3whppcm">•</span> Notification if issues are found</li>
                  </ul>
                  <p className="text-xs font-medium text-white/60 italic" data-id="element-256" data-mp="337sne6n2vrcy">
                    No content edits included. Any requested changes billed separately.
                  </p>
                </div>

                {/* Maintenance Plan */}
                <div className="flex flex-col rounded-2xl border border-amber-500/50 bg-black/80 p-6 shadow-lg backdrop-blur-md relative overflow-hidden" data-id="element-257" data-mp="2p7q9evhw5i2z">
                  <div className="absolute top-0 right-0 bg-amber-500/30 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-amber-300 rounded-bl-lg" data-id="element-258" data-mp="2kf6wm0r12qz8">Popular</div>
                  <h4 className="font-[DM_Serif_Display] text-2xl text-white" data-id="element-259" data-mp="1rr1ab9kbstxa">Maintenance Plan</h4>
                  <div className="my-4 text-3xl font-bold text-amber-300" data-id="element-260" data-mp="3rtal2zf350ha">$49<span className="text-lg text-white/50" data-id="element-261" data-mp="nixo7sp01y6">/mo</span></div>
                  <ul className="mb-6 flex-1 space-y-3 text-sm text-white/80" data-id="element-262" data-mp="lo98ji7prpc">
                    <li className="flex items-start gap-2" data-id="element-263" data-mp="5f0y8uqt00im"><span className="text-amber-500" data-id="element-264" data-mp="9g2ynewgiim6">•</span> Everything in the Monitoring Plan</li>
                    <li className="flex items-start gap-2" data-id="element-265" data-mp="38v7skanbwleq"><span className="text-amber-500" data-id="element-266" data-mp="38w9dd6u5cm9n">•</span> Minor text updates & photo replacements</li>
                    <li className="flex items-start gap-2" data-id="element-267" data-mp="3dv3u838k00r4"><span className="text-amber-500" data-id="element-268" data-mp="3dw5jpcqbgpsd">•</span> Contact information changes</li>
                    <li className="flex items-start gap-2" data-id="element-269" data-mp="3hy0judwigx4k"><span className="text-amber-500" data-id="element-270" data-mp="3mvijfdon1zzw">•</span> Up to 30 minutes of edits per month</li>
                  </ul>
                  <p className="text-xs font-medium text-amber-200/80 italic" data-id="element-271" data-mp="x4vnysikps7t">
                    (This is only necessary if you think you are going to need changes throughout the month, like an "Employee of the Month" update, etc.)
                  </p>
                </div>

                {/* Care Plan */}
                <div className="flex flex-col rounded-2xl border border-white/10 bg-black/60 p-6 shadow-lg backdrop-blur-md" data-id="element-272" data-mp="17a5egxy4uiz1">
                  <h4 className="font-[DM_Serif_Display] text-2xl text-white" data-id="element-273" data-mp="12giyavsrfwyh">Care Plan</h4>
                  <div className="my-4 text-3xl font-bold text-amber-300" data-id="element-274" data-mp="yf7fpa8b0bzs">$99<span className="text-lg text-white/50" data-id="element-275" data-mp="tj8lh60anzrg">/mo</span></div>
                  <ul className="mb-6 flex-1 space-y-3 text-sm text-white/80" data-id="element-276" data-mp="ojdyvenjxwc7">
                    <li className="flex items-start gap-2" data-id="element-277" data-mp="olgse8nwy7lo"><span className="text-amber-500" data-id="element-278" data-mp="jj9gfj332rjc">•</span> Everything in the Maintenance Plan</li>
                    <li className="flex items-start gap-2" data-id="element-279" data-mp="fjhdpn0dfflw"><span className="text-amber-500" data-id="element-280" data-mp="fk90q9fwk50k">•</span> Priority support</li>
                    <li className="flex items-start gap-2" data-id="element-281" data-mp="2dxip1mzgm3qe"><span className="text-amber-500" data-id="element-282" data-mp="29wgyrvnit20k">•</span> Up to 1 hour of edits per month</li>
                    <li className="flex items-start gap-2" data-id="element-283" data-mp="2t572u1bxch45"><span className="text-amber-500" data-id="element-284" data-mp="2t69s64tlmbhy">•</span> New sections, pages, and small additions</li>
                    <li className="flex items-start gap-2" data-id="element-285" data-mp="2x5r1xag25usx"><span className="text-amber-500" data-id="element-286" data-mp="2x8wlyb9ty6hs">•</span> Ongoing website optimization</li>
                  </ul>
                  <p className="text-xs font-medium text-white/60 italic" data-id="element-287" data-mp="2oei9shkljc2w">
                    (This plan is only necessary if you're doing consistent changes throughout the month. If you're a business that's constantly moving and changing, this is for you.)
                  </p>
                </div>
              </div>

              <div className="mt-10 rounded-xl bg-black/40 p-5 text-sm text-white/70 border border-white/5" data-id="element-288" data-mp="20h2xxi16g82i">
                <strong className="text-white" data-id="element-289" data-mp="25eup29tzzq5n">Need extra help?</strong> Any additional edits on top of your plan are $50.00 an hour with a 30-minute minimum. Most changes can be done in less than 30 minutes!
              </div>
            </section>

          </div>
        </div>
      </motion.div>
    </motion.div>;
}

// --- About Dialog ---
function AboutDialog({
  onClose
}: {
  onClose: () => void;
}) {
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.5
  }} className="fixed inset-0 z-[400] flex flex-col items-center justify-center p-4 sm:p-6" role="dialog" aria-modal="true" aria-labelledby="about-title" data-id="element-290" data-mp="8uehv35oy2g6">
      {/* Close Button */}
      <button onClick={onClose} className="absolute top-6 right-6 z-50 inline-flex size-10 items-center justify-center rounded-full border border-white/20 bg-black/40 text-white/90 shadow-lg backdrop-blur-md transition-colors hover:bg-black/60 hover:text-white" aria-label="Close about dialog" data-id="element-291" data-mp="rjuweue7nvfl">
        <XIcon size={20} data-id="element-292" data-mp="37bs6hct680mo" />
      </button>

      {/* Transparent Content Container */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      y: 20
    }} transition={{
      duration: 0.5,
      delay: 0.2
    }} className="relative z-10 flex h-full max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden" data-id="element-293" data-mp="339x9gki9w9ep">
        <div className="flex-1 overflow-y-auto p-6 text-sm leading-relaxed text-white sm:p-10 sm:text-base [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] [text-shadow:0_2px_5px_rgba(0,0,0,1),0_0_15px_rgba(0,0,0,0.8)]" data-id="element-294" data-mp="1rj4qnyixrqfr">
          <div className="mx-auto max-w-2xl space-y-16 pb-20 font-medium tracking-wide" data-id="element-295" data-mp="21ezd74qa65nt">
            
            <section data-id="element-296" data-mp="1wg4xibnm0x7x">
              <h3 className="mb-6 font-[DM_Serif_Display] text-4xl text-amber-400 drop-shadow-lg sm:text-5xl" data-id="element-297" data-mp="2b6lw7n8v1914">Our Mission</h3>
              <p className="mb-5 text-lg font-medium sm:text-xl sm:leading-relaxed" data-id="element-298" data-mp="268tuy2mbsak9">
                At Websites by Mandy, our mission is to help small businesses build a strong foundation from the very beginning by providing accessible, professional websites that give them the visibility they need to get their business off the ground.
              </p>
              <p className="text-lg font-medium sm:text-xl sm:leading-relaxed" data-id="element-299" data-mp="2ysfpyj7n4tdv">
                We believe every great business deserves a place to be seen, because before your business can grow, people have to know you exist.
              </p>
            </section>

            <section data-id="element-300" data-mp="2ttl0wzj2y8lb">
              <h3 className="mb-6 font-[DM_Serif_Display] text-4xl text-amber-400 drop-shadow-lg sm:text-5xl" data-id="element-301" data-mp="2tx0hk6sejpou">About Me</h3>
              <p className="mb-5 text-2xl font-bold text-white drop-shadow-lg" data-id="element-302" data-mp="2ku7qa3j7zdz0">Hi, I'm Mandy.</p>
              <p className="mb-5 text-base sm:text-lg sm:leading-relaxed" data-id="element-303" data-mp="265v1prmgoxej">
                I’m an entrepreneur, creator, and the woman behind Websites by Mandy. Like a lot of small-business owners, I didn’t start out knowing exactly what I was doing—I learned by building, experimenting, making mistakes, starting over, and figuring things out along the way. When I started my own business, I eventually realized that one of the very first things I should have created was a professional place online where people could actually find me, understand what I offered, and connect with my business.
              </p>
              <p className="mb-5 text-base sm:text-lg sm:leading-relaxed" data-id="element-304" data-mp="3u2uip1laq2hz">
                That experience is a huge part of why Websites by Mandy exists.
              </p>
              <p className="mb-5 text-base sm:text-lg sm:leading-relaxed" data-id="element-305" data-mp="qmne8sv24185">
                I know what it feels like to have big ideas without a big-business budget or an entire team behind you. I also know how overwhelming technology can feel when you're already trying to handle everything else that comes with building a business. I've spent years creating online, building brands, learning new technology, and finding creative ways to turn ideas into something real—and now I get to use everything I've learned to help other entrepreneurs do the same.
              </p>
              <p className="mb-8 text-base sm:text-lg sm:leading-relaxed" data-id="element-306" data-mp="2xbs9i7i0tn6a">
                I don't believe you should have to be a huge company to have a beautiful, professional online presence. You can start small and still show up like you mean business.
              </p>
              <p className="text-xl font-bold text-amber-300 drop-shadow-lg sm:text-2xl sm:leading-relaxed" data-id="element-307" data-mp="1luxonoa2g8sg">
                That's what Websites by Mandy is all about: helping small businesses take that first important step toward being seen.
              </p>
            </section>

          </div>
        </div>
      </motion.div>
    </motion.div>;
}

// --- Terms & Conditions Dialog ---
function TermsDialog({
  onClose
}: {
  onClose: () => void;
}) {
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2
  }} className="fixed inset-0 z-[400] flex items-center justify-center bg-black/80 p-4 sm:p-6 backdrop-blur-md" role="dialog" aria-modal="true" aria-labelledby="terms-title" data-id="element-308" data-mp="29l1r3sbiq2hv">
      <motion.div initial={{
      opacity: 0,
      y: 20,
      scale: 0.95
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} exit={{
      opacity: 0,
      y: 20,
      scale: 0.95
    }} transition={{
      duration: 0.3,
      ease: [0.22, 1, 0.36, 1]
    }} className="relative flex h-full max-h-[85vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-white/10 bg-zinc-900 shadow-2xl" data-id="element-309" data-mp="3g0x7s5uumuh8">
        <div className="flex items-center justify-between border-b border-white/10 bg-zinc-900/80 px-6 py-4 backdrop-blur-sm" data-id="element-310" data-mp="2xca49rsbmd5r">
          <h2 id="terms-title" className="font-[DM_Serif_Display] text-2xl text-white" data-id="element-311" data-mp="371tniyjy4915">Terms & Conditions</h2>
          <button onClick={onClose} className="inline-flex size-8 items-center justify-center rounded-full bg-white/10 text-white/70 transition-colors hover:bg-white/20 hover:text-white" aria-label="Close terms dialog" data-id="element-312" data-mp="32bda3eg4knes">
            <XIcon size={18} data-id="element-313" data-mp="29kbrt4h4ybr1" />
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto p-6 text-sm leading-relaxed text-white/80 sm:p-8 sm:text-base" data-id="element-314" data-mp="p3lrw5ci8i7w">
          <div className="mx-auto max-w-3xl space-y-6" data-id="element-315" data-mp="twoq4qs88fuf">
            <p className="font-bold text-white" data-id="element-316" data-mp="g48pfwmsstwo">Websites by Mandy — Terms & Conditions</p>
            <p className="text-xs text-white/50" data-id="element-317" data-mp="k50zut7ep4uj">Last Updated: August 15, 2026</p>
            <p data-id="element-318" data-mp="17uvdhczjuxfu">By purchasing website design or related services from Websites by Mandy (“Websites by Mandy,” “we,” “us,” or “our”), you agree to the following Terms & Conditions.</p>

            <h3 className="pt-4 font-[DM_Serif_Display] text-xl text-white" data-id="element-319" data-mp="1crvidkknxu5d">1. Website Mock-Up & Approval</h3>
            <p data-id="element-320" data-mp="ywjbyfx5af8m">Before paid development begins, Websites by Mandy may provide the client with an initial website mock-up or concept.</p>
            <p data-id="element-321" data-mp="3jk63igo2ym0a">The mock-up is intended to establish the overall design direction, style, layout, and appearance of the website.</p>
            <p data-id="element-322" data-mp="3ol3kwr19nc0q">Once the client approves the mock-up, the project moves into development and the payment terms below apply.</p>

            <h3 className="pt-4 font-[DM_Serif_Display] text-xl text-white" data-id="element-323" data-mp="1u54uj90zalzz">2. Payment</h3>
            <p data-id="element-324" data-mp="1p6adabint5hz">After the client approves the mock-up:</p>
            <ul className="list-disc space-y-2 pl-5" data-id="element-325" data-mp="1kcfk8hh5ze68">
              <li data-id="element-326" data-mp="1kb4ien7a4ghr">50% of the agreed project price is due before development begins.</li>
              <li data-id="element-327" data-mp="1g9szt1mtovj2">The remaining 50% is due when the website is completed and before final delivery, transfer, publication, or release of the completed project, unless otherwise agreed to in writing.</li>
            </ul>
            <p data-id="element-328" data-mp="11emvqk7af6sd">Work will not begin until the initial 50% payment has been received.</p>
            <p data-id="element-329" data-mp="11gg0ftycbgxq">Websites by Mandy may withhold final files, website access, transfers, deployment, or other project deliverables until the outstanding balance has been paid in full.</p>

            <h3 className="pt-4 font-[DM_Serif_Display] text-xl text-white" data-id="element-330" data-mp="2vqjpijt7coql">3. Refunds & Cancellations</h3>
            <p data-id="element-331" data-mp="2roy7yl5q3ma2">Because website design involves customized work performed specifically for each client, payments for work that has already been performed are non-refundable.</p>
            <p data-id="element-332" data-mp="igtes1llbhre">Once the client approves the mock-up and Websites by Mandy begins development, the initial payment compensates Websites by Mandy for time, design, development, creative work, and other services performed on the project.</p>
            <p data-id="element-333" data-mp="3qptb3xm78wvw">Once the website has been completed, no refunds will be provided for completed work.</p>
            <p data-id="element-334" data-mp="3vowd196vumzn">Approval of a mock-up does not mean that every minor detail of the completed website will be identical to the mock-up. Reasonable adjustments may occur during development due to functionality, device responsiveness, platform limitations, or technical requirements.</p>

            <h3 className="pt-4 font-[DM_Serif_Display] text-xl text-white" data-id="element-335" data-mp="8hu3eb9fx80p">4. Client Changes & Revisions</h3>
            <p data-id="element-336" data-mp="1jzdaieq55v4j">The client is responsible for reviewing the website and communicating requested changes during the development process.</p>
            <p data-id="element-337" data-mp="vnuwgtrjhyze">Reasonable revisions that remain within the originally agreed project scope may be included.</p>
            <p data-id="element-338" data-mp="qp016scdyhye">Requests that substantially change the approved design, add new pages or functionality, or otherwise expand the original project may require an additional fee.</p>

            <h3 className="pt-4 font-[DM_Serif_Display] text-xl text-white" data-id="element-339" data-mp="zo4yxwg4i79i">5. AI-Assisted Design & Development</h3>
            <p data-id="element-340" data-mp="1ebztlidoylp8">Websites by Mandy uses modern digital and artificial intelligence tools as part of its creative and development process.</p>
            <p data-id="element-341" data-mp="19gki2x35p1vz">These tools may include AI-assisted website design and development platforms, such as Magic Patterns, as well as artificial intelligence tools used to assist with graphics, imagery, video, animation, written content, layout concepts, coding, and other creative or technical elements.</p>
            <p data-id="element-342" data-mp="3p75ncid0wnxt">By hiring Websites by Mandy, the client acknowledges and agrees that AI-assisted tools may be used in the creation of their website and related materials.</p>
            <p data-id="element-343" data-mp="3b0c251oplaxr">AI-assisted content may occasionally contain inaccuracies, inconsistencies, or other limitations. Websites by Mandy reviews and modifies generated materials as appropriate but does not guarantee that every element of a project was created exclusively by a human or entirely from scratch.</p>
            <p data-id="element-344" data-mp="3679ax07emdkq">Unless specifically agreed to in writing, the client is purchasing the completed website design and services—not exclusive ownership of the underlying third-party software, AI models, platforms, stock assets, fonts, templates, or other third-party materials used to create it.</p>

            <h3 className="pt-4 font-[DM_Serif_Display] text-xl text-white" data-id="element-345" data-mp="2iczhwy60tf7m">6. Domains</h3>
            <p data-id="element-346" data-mp="2narishon8bny">Domain registration is not included unless specifically stated otherwise.</p>
            <p data-id="element-347" data-mp="gl34fj0alva7">Clients who want a custom domain are responsible for purchasing and maintaining their own domain name.</p>
            <p data-id="element-348" data-mp="lgsam56tiafh">Websites by Mandy may recommend third-party domain registrars, including Cloudflare, based on factors such as affordability and available services. However, the client is responsible for selecting their registrar, purchasing the domain, maintaining the account, paying renewal fees, and complying with that provider's terms.</p>
            <p data-id="element-349" data-mp="21zx8yqyb0c5j">Any third-party provider recommended by Websites by Mandy remains an independent company. Websites by Mandy is not responsible for changes in pricing, policies, service availability, outages, account issues, or other actions taken by third-party providers.</p>

            <h3 className="pt-4 font-[DM_Serif_Display] text-xl text-white" data-id="element-350" data-mp="2fz6nznarx07x">7. DNS & Domain Connection</h3>
            <p data-id="element-351" data-mp="2ky9kgjcoty4a">Websites by Mandy will provide the client with the DNS records or other information needed to connect the client's domain to their website.</p>
            <p data-id="element-352" data-mp="lmqbi4rdwpum">The client is responsible for entering those records into their domain registrar or DNS provider unless a separate service has been agreed upon.</p>
            <p data-id="element-353" data-mp="33urtcpxwpt8c">Websites by Mandy will provide the necessary information and reasonable instructions but cannot be responsible for errors caused by incorrectly entered DNS records, registrar restrictions, DNS propagation delays, expired domains, account access problems, or issues originating with the client's domain provider.</p>

            <h3 className="pt-4 font-[DM_Serif_Display] text-xl text-white" data-id="element-354" data-mp="2q4ofond3f7ui">8. Client Responsibilities</h3>
            <p data-id="element-355" data-mp="3mskx1ugcu76s">The client is responsible for providing accurate information and any necessary content, images, logos, business information, account access, approvals, and other materials reasonably required to complete the project.</p>
            <p data-id="element-356" data-mp="38tl62g5ddg7g">The client is also responsible for reviewing the website before final approval and ensuring that business information, pricing, services, contact information, legal disclosures, and other client-provided information are accurate.</p>

            <h3 className="pt-4 font-[DM_Serif_Display] text-xl text-white" data-id="element-357" data-mp="1xdo51tk9b5tv">9. Third-Party Services</h3>
            <p data-id="element-358" data-mp="1yw4q7f0xim6t">Websites may rely on third-party platforms and services for functions such as hosting, domains, forms, analytics, payments, email, integrations, APIs, AI functionality, or other features.</p>
            <p data-id="element-359" data-mp="1punc22730ukr">Websites by Mandy cannot guarantee the continued availability or performance of third-party services and is not responsible for outages, pricing changes, policy changes, discontinued features, or other actions outside of our control.</p>

            <h3 className="pt-4 font-[DM_Serif_Display] text-xl text-white" data-id="element-360" data-mp="1kwdhntt6ly07">10. Website Performance & Compatibility</h3>
            <p data-id="element-361" data-mp="175goa0rflcs3">Websites by Mandy will make reasonable efforts to create a functional and responsive website.</p>
            <p data-id="element-362" data-mp="1b6ttc8mp9bxy">However, websites may display or function differently depending on the visitor's device, browser, operating system, internet connection, screen size, third-party services, or future software updates.</p>
            <p data-id="element-363" data-mp="124kkroazrsn7">Perfect compatibility with every device, browser, or future technology cannot be guaranteed.</p>

            <h3 className="pt-4 font-[DM_Serif_Display] text-xl text-white" data-id="element-364" data-mp="ildnx7nqojei">11. No Guarantee of Business Results</h3>
            <p data-id="element-365" data-mp="shqcxs8ca92x">Websites by Mandy does not guarantee that a website will produce a specific number of visitors, customers, sales, search-engine rankings, leads, revenue, or other business results.</p>
            <p data-id="element-366" data-mp="11ierqp5k4dgv">Website performance depends on many factors outside of the design itself, including marketing, competition, pricing, SEO, advertising, customer demand, and the client's business practices.</p>

            <h3 className="pt-4 font-[DM_Serif_Display] text-xl text-white" data-id="element-367" data-mp="1b7ye44shapgh">12. Client Content & Intellectual Property</h3>
            <p data-id="element-368" data-mp="16a6bogu7jky1">The client represents that they have permission to use any logos, photographs, videos, written content, trademarks, or other materials they provide to Websites by Mandy.</p>
            <p data-id="element-369" data-mp="1g83yczwf59oj">The client is responsible for claims arising from materials supplied by the client without the necessary permission or licensing.</p>

            <h3 className="pt-4 font-[DM_Serif_Display] text-xl text-white" data-id="element-370" data-mp="39nxi5581jmn8">13. Portfolio Rights</h3>
            <p data-id="element-371" data-mp="3en1qxur1zid1">Unless otherwise agreed to in writing, Websites by Mandy may display completed website designs, screenshots, or portions of completed projects in its portfolio, website, social media, or marketing materials for the purpose of demonstrating previous work.</p>

            <h3 className="pt-4 font-[DM_Serif_Display] text-xl text-white" data-id="element-372" data-mp="2qsh3extrqjbc">14. Limitation of Liability</h3>
            <p data-id="element-373" data-mp="2vqsdn9nz6vu3">To the fullest extent permitted by applicable law, Websites by Mandy will not be liable for indirect, incidental, consequential, or special damages arising from the client's website or third-party services.</p>
            <p data-id="element-374" data-mp="34olynogai31y">Websites by Mandy's total liability relating to a project will not exceed the amount the client paid Websites by Mandy for the specific project giving rise to the claim, to the extent permitted by law.</p>

            <h3 className="pt-4 font-[DM_Serif_Display] text-xl text-white" data-id="element-375" data-mp="24iigvwo8qvfs">15. Acceptance of These Terms</h3>
            <p data-id="element-376" data-mp="20gn8673zdfrz">Payment of the initial project payment after approval of the mock-up constitutes acknowledgment and acceptance of these Terms & Conditions unless a separate written agreement states otherwise.</p>
            <p data-id="element-377" data-mp="1qs59pwj6bkri">These Terms & Conditions may be updated periodically. The terms applicable to a client project will be those provided or made available when the client enters into the agreement.</p>
            
            <div className="pt-8 pb-4" data-id="element-378" data-mp="1gs4qsbp7v6nu">
              <p className="font-[DM_Serif_Display] text-xl text-white" data-id="element-379" data-mp="1gvir7eebl5sn">Websites by Mandy</p>
              <p className="mt-2 text-sm text-white/60" data-id="element-380" data-mp="1ctoufh78wlxr">For questions regarding these Terms & Conditions, please contact Websites by Mandy using the contact information provided on our website.</p>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>;
}

// --- Contact Dialog ---
function ContactDialog({
  onClose
}: {
  onClose: () => void;
}) {
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.5
  }} className="fixed inset-0 z-[400] flex flex-col items-center justify-center p-4 sm:p-6" role="dialog" aria-modal="true" aria-labelledby="contact-title" data-id="element-381" data-mp="3l9klgvz95dxv">
      {/* Close Button */}
      <button onClick={onClose} className="absolute top-6 right-6 z-50 inline-flex size-10 items-center justify-center rounded-full border border-white/20 bg-black/40 text-white/90 shadow-lg backdrop-blur-md transition-colors hover:bg-black/60 hover:text-white" aria-label="Close contact dialog" data-id="element-382" data-mp="8th2hegc7u6p">
        <XIcon size={20} data-id="element-383" data-mp="3t2rbnjfw44g" />
      </button>

      {/* Content Container */}
      <motion.div initial={{
      opacity: 0,
      y: 20,
      scale: 0.95
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} exit={{
      opacity: 0,
      y: 20,
      scale: 0.95
    }} transition={{
      duration: 0.4,
      delay: 0.1
    }} className="relative z-10 w-full max-w-3xl overflow-hidden rounded-2xl border border-white/20 bg-white/10 shadow-2xl backdrop-blur-md" data-id="element-384" data-mp="2jlihin4kviad">
        <div className="h-[85vh] max-h-[800px] w-full" data-id="element-385" data-mp="2q1s5crnchaiu">
          <iframe id="JotFormIFrame-262267272074054" title="Contact Form" allowTransparency={true} allowFullScreen={true} allow="geolocation; microphone; camera" src="https://form.jotform.com/262267272074054" frameBorder="0" style={{
          minWidth: '100%',
          height: '100%',
          border: 'none'
        }} scrolling="yes" data-id="element-386" data-mp="2q18xy4vbm4j2" />
        </div>
      </motion.div>
    </motion.div>;
}

// --- Main App ---
export function App() {
  const [isVideoEnded, setIsVideoEnded] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [showPricing, setShowPricing] = useState(false);
  const [tourProject, setTourProject] = useState<Project | null>(null);
  const [liveProject, setLiveProject] = useState<Project | null>(null);
  const videoRef = React.useRef<HTMLVideoElement>(null);
  useEffect(() => {
    document.title = "Websites by Mandy";
    if (videoRef.current) {
      videoRef.current.playbackRate = 1.25; // Speeds up the video by 25%
    }
  }, []);
  const isScreenActive = showMenu || tourProject !== null || liveProject !== null;
  const showSharedVideo = showAbout || showContact || showPricing;
  const goHome = () => {
    setShowMenu(false);
    setShowContact(false);
    setShowTerms(false);
    setShowPrivacy(false);
    setShowAbout(false);
    setShowPricing(false);
    setTourProject(null);
    setLiveProject(null);
  };
  return <main className="relative min-h-screen w-full overflow-hidden bg-black font-[DM_Sans] text-white" data-id="element-387" data-mp="1vqx2561eu7dv">
      
      {/* 1. The Garden Park Background Video */}
      <motion.video ref={videoRef} src="https://cdn.magicpatterns.com/uploads/erZwpuUcX9wLru2JRi5LB2/lv_0_20260815190747.mp4" autoPlay muted playsInline onEnded={() => setIsVideoEnded(true)} animate={{
      scale: isVideoEnded ? 1.4 : 1,
      filter: isVideoEnded ? "blur(12px)" : "blur(0px)",
      opacity: isVideoEnded ? 0 : 1
    }} transition={{
      duration: 2,
      ease: [0.25, 1, 0.5, 1]
    }} className="fixed inset-0 h-full w-full object-cover origin-[50%_35%]" data-id="element-388" data-mp="3b8tm863a7xto" />

      {/* 2. High-Res Zoomed Image */}
      <AnimatePresence data-id="element-389" data-mp="3kat7xbp8rq2y">
        {isVideoEnded && <motion.img src="https://cdn.magicpatterns.com/uploads/9v5L4wLa8roanet7cQpHcD/image.png" alt="Crisp view of the drive-in screen" initial={{
        opacity: 0,
        scale: 0.85
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 2,
        ease: [0.25, 1, 0.5, 1]
      }}
      // Use object-contain on mobile to ensure the full width (text and heart) is visible without cropping.
      // Use object-cover on desktop where the aspect ratio is wider.
      className="fixed inset-0 z-10 h-full w-full object-contain sm:object-cover origin-center" data-id="element-390" data-mp="3k7yjza70rn4s" />}
      </AnimatePresence>

      {/* 3. The TV Screen Container (Exactly matches the wooden frame in the image) */}
      <AnimatePresence data-id="element-391" data-mp="2m441m0hb8us0">
        {isVideoEnded && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.8,
        duration: 1
      }}
      // On desktop, this perfectly positions the div over the wooden screen.
      // On mobile, when active, it breaks out to fill the entire screen for usability.
      // When inactive on mobile, it centers perfectly over the object-contain image.
      className={`transition-all duration-700 overflow-hidden ${isScreenActive ? 'fixed inset-0 z-50 w-full h-full max-w-none translate-x-0 translate-y-0 bg-zinc-950 sm:absolute sm:left-1/2 sm:top-1/2 sm:z-20 sm:w-[82vw] sm:max-w-[1200px] sm:-translate-x-1/2 sm:-translate-y-[56%] sm:aspect-[1.75/1] sm:shadow-[0_0_100px_rgba(0,0,0,0.8)]' : 'absolute left-1/2 top-1/2 z-20 w-[100vw] sm:w-[82vw] max-w-[1200px] -translate-x-1/2 -translate-y-[56%] aspect-[1.75/1] bg-transparent shadow-none'}`} data-id="element-392" data-mp="2m5f7q6y8aema">
            <AnimatePresence mode="wait" data-id="element-393" data-mp="439z79usyshl">
              {tourProject ? <InScreenTour key="tour" project={tourProject} onClose={() => setTourProject(null)} data-id="element-394" data-mp="8wux71webmgs" /> : liveProject ? <InScreenLivePreview key="live" project={liveProject} onClose={() => setLiveProject(null)} data-id="element-395" data-mp="hx1bur3tyshj" /> : showMenu ? <InScreenMenu key="menu" onSelectTour={setTourProject} onSelectLive={setLiveProject} onClose={() => setShowMenu(false)} data-id="element-396" data-mp="mx5rkewwzj6p" /> : <motion.div key="home-buttons" initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} transition={{
            delay: 1.5,
            duration: 0.8
          }} className="absolute top-[58%] sm:top-[62%] left-0 right-0 flex flex-row items-center justify-center gap-2 sm:gap-4 px-2 sm:px-4" data-id="element-397" data-mp="2exno1ks23yfb">
                  <button onClick={() => setShowMenu(true)} className="inline-flex items-center gap-1.5 sm:gap-2 rounded-full border border-white/20 bg-black/40 px-3 py-2 sm:px-6 sm:py-3 text-[10px] sm:text-sm font-bold text-white shadow-lg backdrop-blur-md transition-all hover:scale-105 hover:bg-black/60 whitespace-nowrap" data-id="element-398" data-mp="2xn3pqbfh9wxb">
                    <MonitorIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" data-id="element-399" data-mp="1cblsk7bz9ghw" /> View Portfolio
                  </button>
                  <button onClick={() => setShowContact(true)} className="inline-flex items-center gap-1.5 sm:gap-2 rounded-full bg-amber-500 px-3 py-2 sm:px-6 sm:py-3 text-[10px] sm:text-sm font-bold text-white shadow-[0_0_20px_rgba(245,158,11,0.4)] transition-all hover:scale-105 hover:bg-amber-600 whitespace-nowrap" data-id="element-400" data-mp="3nzgubuguogj3">
                    <SparklesIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" data-id="element-401" data-mp="357ekb1ypryht" /> Get Yours Today
                  </button>
                </motion.div>}
            </AnimatePresence>
          </motion.div>}
      </AnimatePresence>

      {/* Top Navigation Menu */}
      <AnimatePresence data-id="element-402" data-mp="308behfawto8s">
        {isVideoEnded && <motion.div initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.5,
        duration: 0.8
      }} className="absolute top-4 left-0 right-0 z-[500] flex justify-center px-4" data-id="element-403" data-mp="30939anwonhy0">
            <nav className="flex items-center gap-4 sm:gap-6 rounded-full border border-white/10 bg-black/50 px-6 py-3 backdrop-blur-md shadow-lg overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]" data-id="element-404" data-mp="9qu10zols06j">
              <button onClick={goHome} className="shrink-0 text-[10px] sm:text-xs font-bold text-white/80 hover:text-amber-400 transition-colors uppercase tracking-widest" data-id="element-405" data-mp="3vvxqsms9hjmg">Home</button>
              <button onClick={() => {
            goHome();
            setShowMenu(true);
          }} className="shrink-0 text-[10px] sm:text-xs font-bold text-white/80 hover:text-amber-400 transition-colors uppercase tracking-widest" data-id="element-406" data-mp="3mynauo4i16b7">Portfolio</button>
              <button onClick={() => {
            goHome();
            setShowPricing(true);
          }} className="shrink-0 text-[10px] sm:text-xs font-bold text-white/80 hover:text-amber-400 transition-colors uppercase tracking-widest" data-id="element-407" data-mp="3d56kju3km9ca">Pricing</button>
              <button onClick={() => {
            goHome();
            setShowAbout(true);
          }} className="shrink-0 text-[10px] sm:text-xs font-bold text-white/80 hover:text-amber-400 transition-colors uppercase tracking-widest" data-id="element-408" data-mp="2z7roi6itb3bc">About</button>
              <button onClick={() => {
            goHome();
            setShowContact(true);
          }} className="shrink-0 text-[10px] sm:text-xs font-bold text-white/80 hover:text-amber-400 transition-colors uppercase tracking-widest" data-id="element-409" data-mp="1iqautbqer04n">Contact</button>
            </nav>
          </motion.div>}
      </AnimatePresence>

      {/* 4. Footer Links (Terms & Privacy) */}
      <AnimatePresence data-id="element-410" data-mp="v3b18idd8qc8">
        {isVideoEnded && !isScreenActive && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        delay: 2,
        duration: 1
      }} className="absolute bottom-8 sm:bottom-4 left-0 right-0 z-20 flex flex-col items-center gap-2 px-4" data-id="element-411" data-mp="101m9ioo0issc">
            <div className="flex items-center gap-4 [text-shadow:0_1px_2px_rgba(0,0,0,1),0_0_8px_rgba(0,0,0,0.9)]" data-id="element-412" data-mp="ca776vw8kbh3">
              <button onClick={() => setShowTerms(true)} className="text-xs font-bold text-white transition-colors hover:text-white/80 hover:underline" data-id="element-413" data-mp="h88q9gtnwask">
                Terms & Conditions
              </button>
              <span className="text-xs font-bold text-white/70" data-id="element-414" data-mp="izgkemvneqzm">•</span>
              <button onClick={() => setShowPrivacy(true)} className="text-xs font-bold text-white transition-colors hover:text-white/80 hover:underline" data-id="element-415" data-mp="e03v7vhb17tv">
                Privacy Policy
              </button>
            </div>
            <a href="https://trippinwithmandy.com" target="_blank" rel="noopener noreferrer" className="text-[10px] font-bold text-white text-center transition-colors hover:text-white/80 hover:underline [text-shadow:0_1px_2px_rgba(0,0,0,1),0_0_8px_rgba(0,0,0,0.9)]" data-id="element-416" data-mp="3igeuboj0wqcu">
              Made with love and a whole lot of grit by Trippin' With Mandy™ LLC. © 2026
            </a>
          </motion.div>}
      </AnimatePresence>

      {/* 5. Shared Background Video for About & Contact Pages */}
      <AnimatePresence data-id="element-417" data-mp="2tmk71slj31wa">
        {showSharedVideo && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.5
      }} className="fixed inset-0 z-[350] bg-black" data-id="element-418" data-mp="2flpvx273imtx">
            <video src="https://cdn.magicpatterns.com/uploads/wAVEWvFZneY42MFBT2gmfB/lv_0_20260816011659.mp4" autoPlay loop muted playsInline className="absolute inset-0 h-full w-full object-cover" data-id="element-419" data-mp="38bgd5k40y4u9" />
            {/* Subtle overlay to ensure text readability against the bright video */}
            <div className="absolute inset-0 bg-black/50" data-id="element-420" data-mp="3r8zowvhva1kd" />
          </motion.div>}
      </AnimatePresence>

      {/* 6. Modals (Appear over everything) */}
      <AnimatePresence data-id="element-421" data-mp="1vxq5zwhddmw0">
        {showContact && <ContactDialog onClose={() => setShowContact(false)} data-id="element-422" data-mp="1vyrtj0fko1um" />}
        {showTerms && <TermsDialog onClose={() => setShowTerms(false)} data-id="element-423" data-mp="1r6hvsy87txhs" />}
        {showPrivacy && <PrivacyDialog onClose={() => setShowPrivacy(false)} data-id="element-424" data-mp="1m7wvkysyrdtc" />}
        {showAbout && <AboutDialog onClose={() => setShowAbout(false)} data-id="element-425" data-mp="1m5u8amj73ysb" />}
        {showPricing && <PricingDialog onClose={() => setShowPricing(false)} data-id="element-426" data-mp="1ha52i1gl1lnk" />}
      </AnimatePresence>

    </main>;
}